import React, { useState } from 'react';
import { HERO_CONTENT, IMAGES, SOCIAL_LINKS } from '../../../constants';
import { Button, Icon } from '../../common';

// Instagram SVG Icon
const InstagramIcon = () => (
  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

// YouTube SVG Icon
const YouTubeIcon = () => (
  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

/**
 * Animated Title Component - Toggles between MATISA and ASMITA
 * Letters rearrange with smooth animation on click
 */
const AnimatedTitle = () => {
  const [isAsmita, setIsAsmita] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Letters with their positions in each state
  // MATISA: M(0) A(1) T(2) I(3) S(4) A(5)
  // ASMITA: A(0) S(1) M(2) I(3) T(4) A(5)
  const letters = [
    { char: 'M', matisaOrder: 0, asmitaOrder: 2, delay: 0 },
    { char: 'A', matisaOrder: 1, asmitaOrder: 0, delay: 50 },
    { char: 'T', matisaOrder: 2, asmitaOrder: 4, delay: 100 },
    { char: 'I', matisaOrder: 3, asmitaOrder: 3, delay: 150 },
    { char: 'S', matisaOrder: 4, asmitaOrder: 1, delay: 200 },
    { char: 'A', matisaOrder: 5, asmitaOrder: 5, delay: 250, isSecondA: true },
  ];

  const handleClick = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIsAsmita(!isAsmita);
    setTimeout(() => setIsAnimating(false), 1800);
  };

  return (
    <h1 
      className="text-7xl sm:text-8xl font-black tracking-tighter text-white drop-shadow-[0_10px_30px_rgba(0,0,0,0.5)] leading-[0.8] z-10 relative uppercase font-display cursor-pointer select-none group"
      onClick={handleClick}
      title="Click to reveal the secret!"
    >
      {/* Animated glow on click */}
      <span 
        className={`absolute inset-0 bg-primary/30 blur-[60px] rounded-full transition-opacity duration-1000 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
      />
      
      <span className="inline-flex relative">
        {letters.map((letter, index) => {
          // Check if this letter moves position
          const moves = letter.matisaOrder !== letter.asmitaOrder;
          
          return (
            <span
              key={`${letter.char}-${index}`}
              className={`
                inline-block relative
                transition-all ease-[cubic-bezier(0.22,1,0.36,1)]
              `}
              style={{
                order: isAsmita ? letter.asmitaOrder : letter.matisaOrder,
                transitionDuration: '1400ms',
                transitionDelay: `${letter.delay}ms`,
                transform: isAnimating && moves 
                  ? 'scale(1.1) translateY(-4px)' 
                  : 'scale(1) translateY(0)',
                textShadow: isAnimating
                  ? '0 0 40px rgba(13, 108, 242, 0.8), 0 10px 30px rgba(0,0,0,0.5)'
                  : '0 10px 30px rgba(0,0,0,0.5)',
              }}
            >
              {letter.char}
            </span>
          );
        })}
      </span>
    </h1>
  );
};

/**
 * Hero Section - Full screen hero with background image
 * Main landing area with title, tagline, and CTAs
 */
const HeroSection = ({ onBookShow, onWatchPerformances }) => {
  return (
    <section className="relative h-screen-safe min-h-[640px] w-full flex flex-col justify-end pb-10 overflow-hidden shrink-0">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-background-dark/20 to-transparent z-10" />
        <div className="absolute inset-0 bg-black/30 z-10" />
        
        {/* Image */}
        <div 
          className="w-full h-full bg-cover bg-no-repeat transform scale-105"
          style={{ 
            backgroundImage: `url('${IMAGES.heroBackground}')`,
            backgroundPosition: 'center calc(50% - 6vh)'
          }}
          role="img"
          aria-label="Matisa playing acoustic guitar on stage with warm moody lighting"
        />
      </div>

      {/* Main Content */}
      <div className="relative z-20 px-6 flex flex-col items-center text-center gap-10 animate-fade-in-up">
        {/* Title Block */}
        <div className="relative pt-12 pb-2 w-full flex flex-col items-center">
          <div className="relative transform -rotate-2 hover:rotate-0 transition-transform duration-700 ease-out">
            <AnimatedTitle />
            
            {/* Script Subtitle */}
            <div className="absolute -bottom-8 -right-4 sm:-right-8 z-20 rotate-[-5deg]">
              <span className="font-script text-5xl sm:text-7xl text-primary drop-shadow-[0_2px_15px_rgba(13,108,242,0.8)] leading-none">
                {HERO_CONTENT.subtitle}
              </span>
            </div>
            
            {/* Glow Effect */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/20 blur-[80px] rounded-full -z-10 mix-blend-screen" />
          </div>

          {/* Tagline */}
          <div className="mt-14 flex flex-col items-center gap-2 relative z-10">
            <div className="flex flex-col items-center justify-center gap-1">
              <span className="text-white/90 font-bold uppercase tracking-[0.35em] text-[10px] sm:text-xs drop-shadow-md">
                {HERO_CONTENT.tagline}
              </span>
            </div>
          </div>
        </div>

        {/* Quote and CTAs */}
        <div className="space-y-5 w-full flex flex-col items-center max-w-sm">
          <p className="text-gray-200 text-sm font-medium italic opacity-90 drop-shadow-sm max-w-[280px]">
            {HERO_CONTENT.quote}
          </p>
          
          <div className="w-full space-y-3 pt-2">
            <Button 
              variant="primary" 
              size="lg" 
              icon="calendar_today" 
              fullWidth
              onClick={onBookShow}
            >
              {HERO_CONTENT.cta.primary}
            </Button>
            
            <Button 
              variant="secondary" 
              size="lg" 
              icon="play_circle" 
              fullWidth
              onClick={onWatchPerformances}
            >
              {HERO_CONTENT.cta.secondary}
            </Button>
          </div>

          {/* Social Links */}
          <div className="flex items-center justify-center gap-3 mt-2">
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/60 hover:text-primary transition-colors group"
            >
              <InstagramIcon />
              <span className="text-sm font-semibold tracking-wide group-hover:underline">
                Instagram
              </span>
            </a>
            <span className="text-white/30">|</span>
            <a
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-white/60 hover:text-red-500 transition-colors group"
            >
              <YouTubeIcon />
              <span className="text-sm font-semibold tracking-wide group-hover:underline">YouTube</span>
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button 
          className="pt-2 pb-4 cursor-pointer hover:scale-110 transition-transform focus:outline-none"
          onClick={(e) => {
            // Find the scrollable container (MobileContainer's inner div)
            const scrollContainer = e.target.closest('.overflow-y-auto');
            if (scrollContainer) {
              scrollContainer.scrollTo({
                top: scrollContainer.clientHeight + 50,
                behavior: 'smooth'
              });
            }
          }}
          aria-label="Scroll to content"
        >
          <Icon 
            name="keyboard_arrow_down" 
            className="text-white/30 animate-bounce hover:text-white/50 transition-colors" 
            size="lg"
          />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
